import React, { useState, useEffect } from 'react';
import { ENEMIES, WEAPONS } from '../game/data';
import { STARTER_WEAPONS } from '../game/data_ext2';
import { rollGearDrop } from '../game/gear';
import { rollRune } from '../game/runes';
import { rollAttachment } from '../game/attachments';
import { patrolSlots, patrolRewardMult } from '../game/base';
import { pushToast } from '../game/toastBus';
import { applyReward } from '../store';
import {
  battlePassReachedNode, battlePassUnclaimedCount, normalizeBpClaimed,
  bpRewardBag, BP_NODE_COUNT, codexUnclaimedCount,
} from '../game/campClaims';
import { Audio } from '../game/audio';
import CardBattle from './CardBattle';
import GameIcon from './GameIcon';
import { fireClaimFx, CAMP_CLAIM_FLY } from './ClaimFx';

// ─── Claim badge — a living waterdrop, not the Eye of Horus ───────────────
// Was a literal Eye of Horus glyph, gold, unrelated to anything else in the
// game's visual language ("brukar va utropstecken eller frågetecken eller
// nått men vill ha nått som är unikt för spelet"). This is a droplet in the
// same teal the XP vessel gauge and every waterdrop icon already use, so a
// "something to claim here" mark reads as house style rather than a
// borrowed generic-UI symbol. The body is a static gradient (cheap — this
// can sit on 5-6 tab buttons at once, so no per-instance rAF loop the way
// the HUD's vessel gauge affords for its one always-on instance); the
// "living" motion is a CSS breathe+glow pulse instead, plus two small
// ripple rings that expand and fade to suggest the water is moving without
// animating any actual path geometry.
export function ClaimBadge({ size = 14 }) {
  return (
    <span className="claim-badge" title="Something to claim">
      <svg viewBox="0 0 20 24" width={size} height={Math.round(size * 1.2)} aria-hidden="true">
        <defs>
          <linearGradient id="claim-badge-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d8f6ff" />
            <stop offset="45%" stopColor="#5fd6ff" />
            <stop offset="100%" stopColor="#0f6fae" />
          </linearGradient>
        </defs>
        <circle className="claim-badge-ring" cx="10" cy="15" r="7" fill="none" stroke="#7adfff" strokeWidth="1" />
        <path
          d="M10 1 C15.5 8.4 18 13.2 18 16.4 A8 8 0 0 1 2 16.4 C2 13.2 4.5 8.4 10 1 Z"
          fill="url(#claim-badge-fill)" stroke="#0a3a55" strokeWidth="1.1" />
        <ellipse cx="7.2" cy="12.5" rx="1.7" ry="2.8" fill="#ffffff" opacity="0.55" />
      </svg>
    </span>
  );
}

// ─── PATROL SYSTEM ────────────────────────────
// Patrol payouts. Longer runs are worth more per hour, not just more in
// total — otherwise there is no reason ever to send anything but the
// shortest one and collect it repeatedly. `chance` entries are rolls rather
// than guarantees, so a long patrol has something to hope for on top of its
// floor.
export const PATROL_DEFS = [
  {
    id: 'shore', name: 'Shore Walk', icon: 'ui_waterdrop', dur: 3 * 60 * 1000,
    rwd: { gold: 220, gearParts: 4 },
    desc: '3m — Walk the wet ring. Back before the next run is over.',
  },
  {
    id: 'scavenge', name: 'Scavenge', icon: 'ui_search', dur: 60 * 60 * 1000,
    rwd: { gold: 800, gearParts: 10, chance: [{ p: 0.30, petFood: 2 }] },
    desc: '1h — Search the ruins for gold, scrap and salvage.',
  },
  {
    id: 'explore', name: 'Explore', icon: 'ui_map', dur: 2 * 60 * 60 * 1000,
    rwd: { gems: 5, gold: 900, gearParts: 22, chance: [{ p: 0.35, rune: true }, { p: 0.25, petFood: 3 }, { p: 0.25, attach: true }] },
    desc: '2h — Venture deep. Gems, scrap, and sometimes a rune.',
  },
  {
    id: 'hunt', name: 'Hunt', icon: 'ui_blade', dur: 3 * 60 * 60 * 1000,
    rwd: { gear: true, gold: 1400, gearParts: 38, slotCoins: 2, chance: [{ p: 0.45, rune: true }, { p: 0.30, egg: true }, { p: 0.40, attach: true }] },
    desc: '3h — Track them down. Equipment, scrap, sometimes an egg.',
  },
  {
    id: 'void', name: 'Void Raid', icon: 'ui_void', dur: 4 * 60 * 60 * 1000,
    rwd: {
      sp: 8, gems: 8, xp: 400, gold: 2200, gearParts: 60, gachaTokens: 1,
      chance: [{ p: 0.6, rune: true }, { p: 0.45, gear: true }, { p: 0.2, flux: 1 }, { p: 0.55, attach: true }],
    },
    desc: '4h — Raid the void. The best of everything, and Flux if you are lucky.',
  },
];

function fmtTimeLeft(startMs, durMs) {
  const rem = Math.max(0, startMs + durMs - Date.now());
  if (rem === 0) return 'COMPLETE';
  const h = Math.floor(rem / 3600000);
  const m = Math.floor((rem % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function pctDone(startMs, durMs) {
  return Math.min(100, ((Date.now() - startMs) / durMs) * 100);
}

export function PatrolPanel({ save, setSave }) {
  const [now, setNow] = useState(Date.now());
  const [claimMsg, setClaimMsg] = useState('');
  const [encounter, setEncounter] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 8000);
    return () => clearInterval(t);
  }, []);

  const missions = save.patrol?.missions || [];
  const active = missions.filter(m => m.status !== 'claimed');
  // Slot count and reward scale both come from the Patrol Post building.
  const maxPatrol = patrolSlots(save);
  const canAdd  = active.length < maxPatrol;
  const hasPet  = (save.pets || []).some(p => p.active);
  const petMult = (hasPet ? 1.2 : 1) * patrolRewardMult(save);

  const liveMissions = missions.map(m =>
    (m.status === 'active' && now - m.startMs >= m.durationMs) ? { ...m, status: 'complete' } : m
  );

  const sendMission = (defId) => {
    if (!canAdd) return;
    const def = PATROL_DEFS.find(d => d.id === defId);
    const newM = {
      id: `p_${Date.now().toString(36)}`,
      defId, startMs: Date.now(), durationMs: def.dur, status: 'active'
    };
    setSave({ ...save, patrol: { missions: [...missions, newM] } });
  };

  // Roughly half of completed patrols run into something on the way back.
  // Rolled at CLAIM time rather than at send time so the outcome cannot be
  // predicted and re-rolled by cancelling and re-sending the mission.
  const claimMission = (mId, lootMult = null, anchor = null) => {
    const m = liveMissions.find(x => x.id === mId);
    if (!m || m.status !== 'complete') return;
    const def = PATROL_DEFS.find(d => d.id === m.defId);
    if (lootMult === null && def.id !== 'shore' && Math.random() < 0.5) {
      const tier = Math.max(0, PATROL_DEFS.indexOf(def) - 1);
      setEncounter({ missionId: mId, tier: Math.max(0, tier) });
      return;
    }
    const mult = lootMult ?? 1;
    let ns = { ...save };
    const got = [];
    // One place grants a reward line, so the guaranteed table and the chance
    // rolls below cannot drift apart in what they support.
    // Encounter outcome scales the whole haul, so a fight you won is visible
    // in every line of the payout rather than as a separate bonus.
    const petMultF = petMult * mult;
    const bag = {};
    const grant = (rwd) => {
      if (rwd.gold)      { bag.gold = (bag.gold || 0) + Math.round(rwd.gold * petMultF); got.push(`Gold ${Math.round(rwd.gold * petMultF)}`); }
      if (rwd.gems)      { bag.gems = (bag.gems || 0) + Math.round(rwd.gems * petMultF); got.push(`Gems ${Math.round(rwd.gems * petMultF)}`); }
      if (rwd.sp)        { bag.sp = (bag.sp || 0) + Math.round(rwd.sp * petMultF); got.push(`SP ${Math.round(rwd.sp * petMultF)}`); }
      if (rwd.gearParts) { bag.gearParts = (bag.gearParts || 0) + Math.round(rwd.gearParts * petMultF); got.push(`Parts ${Math.round(rwd.gearParts * petMultF)}`); }
      if (rwd.slotCoins) { bag.slotCoins = (bag.slotCoins || 0) + rwd.slotCoins; got.push(`Coins ${rwd.slotCoins}`); }
      if (rwd.gachaTokens) { bag.gachaTokens = (bag.gachaTokens || 0) + rwd.gachaTokens; got.push(`Tokens ${rwd.gachaTokens}`); }
      if (rwd.petFood)   { bag.petFood = (bag.petFood || 0) + rwd.petFood; got.push(`Food ${rwd.petFood}`); }
      if (rwd.flux)      { bag.flux = (bag.flux || 0) + rwd.flux; got.push(`Flux ${rwd.flux}`); }
      if (rwd.xp)        { ns.profile = { ...ns.profile, xp: (ns.profile?.xp || 0) + Math.round(rwd.xp * petMultF) }; got.push(`XP ${Math.round(rwd.xp * petMultF)}`); }
      if (rwd.gear)      { ns.gearInventory = [...(ns.gearInventory || []), rollGearDrop(m.durationMs / 1000)]; got.push('Equipment'); }
      if (rwd.rune)      { ns.runeInventory = [...(ns.runeInventory || []), rollRune(ns.attrs?.luck || 0)]; got.push('Rune'); }
      if (rwd.attach)    { ns.attachInventory = [...(ns.attachInventory || []), rollAttachment(ns.attrs?.luck || 0)]; got.push('Attachment'); }
      if (rwd.egg) {
        const rarities = ['common', 'common', 'uncommon', 'rare'];
        const types = ['aquaSprite', 'shadowWisp', 'emberFairy', 'stormHawk', 'lifeSprite'];
        ns.petEggs = [...(ns.petEggs || []), {
          id: `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
          rarity: rarities[(Math.random() * rarities.length) | 0],
          type: types[(Math.random() * types.length) | 0],
        }];
        got.push('Egg');
      }
    };
    grant(def.rwd);
    for (const c of def.rwd.chance || []) {
      if (Math.random() < c.p) grant(c);
    }
    applyReward(ns, bag);
    ns.patrol = { missions: liveMissions.map(x => x.id === mId ? { ...x, status: 'claimed' } : x) };
    setSave(ns);
    setClaimMsg(got.join('  ·  '));
    setTimeout(() => setClaimMsg(''), 4200);
    fireClaimFx(anchor, Object.keys(bag).length ? bag : def.rwd, { flyTo: CAMP_CLAIM_FLY });
    Audio.claimPing?.();
    pushToast({
      key: 'patrol:' + mId, once: true,
      tone: 'gold', icon: def.icon, title: `${def.name} complete`, text: got.join('  ·  '),
    });
  };

  const cancelMission = (mId) => {
    setSave({ ...save, patrol: { missions: missions.filter(x => x.id !== mId) } });
  };

  return (
    <div className="patrol-panel" data-testid="patrol-panel">
      <div className="patrol-header">
        <span className="patrol-title">PATROL MISSIONS</span>
        <span className="patrol-slots">{active.length}/{maxPatrol} slots</span>
        {hasPet && <span className="patrol-pet-bonus" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><GameIcon name="ui_pets" size={13} /> +20% reward (pet active)</span>}
      </div>
      {claimMsg && <div className="patrol-claim-msg" data-testid="patrol-claim">{claimMsg}</div>}
      {encounter && (
        <CardBattle
          pet={(save.pets || []).find(p => p.active) || null}
          tier={encounter.tier}
          onDone={(mult, roundsWon) => {
            const id = encounter.missionId;
            setEncounter(null);
            claimMission(id, mult);
            setTimeout(() => setClaimMsg(m => `${roundsWon}/3 rounds — ${Math.round(mult * 100)}% haul  ·  ${m}`), 0);
          }}
          // Walking away forfeits the fight but not the patrol: you still get
          // the losing share rather than losing hours of waiting outright.
          onClose={() => { const id = encounter.missionId; setEncounter(null); claimMission(id, 0.35); }}
        />
      )}

      {/* Active missions */}
      {liveMissions.filter(m => m.status !== 'claimed').map(m => {
        const def = PATROL_DEFS.find(d => d.id === m.defId);
        const done = m.status === 'complete';
        const pct  = pctDone(m.startMs, m.durationMs);
        const left = fmtTimeLeft(m.startMs, m.durationMs);
        return (
          <div key={m.id} className={`patrol-mission ${done ? 'done' : 'active'}`} data-testid={`patrol-m-${m.defId}`}>
            <span className="patrol-m-icon"><GameIcon name={def.icon} size={20} /></span>
            <div className="patrol-m-info">
              <span className="patrol-m-name">{def.name}</span>
              <span className="patrol-m-status">{done ? 'COMPLETE — CLAIM NOW' : left}</span>
              {!done && <div className="patrol-bar"><div className="patrol-bar-fill" style={{ width: pct + '%' }}/></div>}
            </div>
            {done && (
              <button className="patrol-btn claim" onClick={(e) => claimMission(m.id, null, e.currentTarget)} data-testid={`patrol-claim-${m.defId}`}>
                CLAIM{rwd_preview(def, petMult)}
              </button>
            )}
            {!done && (
              <button className="patrol-btn cancel" onClick={() => cancelMission(m.id)}>ABORT</button>
            )}
          </div>
        );
      })}

      {/* Send mission */}
      {canAdd && (
        <div className="patrol-send-section">
          <div className="patrol-send-title">SEND ON MISSION</div>
          <div className="patrol-defs">
            {PATROL_DEFS.map(def => {
              const already = liveMissions.some(m => m.status !== 'claimed' && m.defId === def.id);
              return (
                <div key={def.id} className={`patrol-def ${already ? 'running' : ''}`} data-testid={`patrol-def-${def.id}`}>
                  <span className="patrol-def-icon"><GameIcon name={def.icon} size={20} /></span>
                  <div className="patrol-def-info">
                    <span className="patrol-def-name">{def.name}</span>
                    <span className="patrol-def-desc">{def.desc}</span>
                    <span className="patrol-def-rwd">{rwd_preview(def, petMult)}</span>
                  </div>
                  <button
                    className="patrol-btn send"
                    onClick={() => sendMission(def.id)}
                    disabled={already}
                    data-testid={`patrol-send-${def.id}`}
                  >
                    {already ? 'ON PATROL' : 'SEND'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!canAdd && liveMissions.every(m => m.status === 'active') && (
        <div className="patrol-full">All patrol slots in use. Return when missions complete.</div>
      )}
    </div>
  );
}

function rwd_preview(def, mult = 1) {
  const r = def.rwd;
  const parts = [];
  if (r.gold)  parts.push(`${Math.round(r.gold  * mult)} Gold`);
  if (r.gems)  parts.push(`${Math.round(r.gems  * mult)} Gems`);
  if (r.sp)    parts.push(`${Math.round(r.sp    * mult)}SP`);
  if (r.xp)    parts.push(`${Math.round(r.xp    * mult)}XP`);
  if (r.gear)  parts.push(`1 Gear`);
  return ' — ' + parts.join(', ');
}

// ─── BATTLE PASS (2 tracks: Free + Premium) ─────────────────────────────
// The game has no real payment gate (Stripe/login bypassed — see App.js),
// so "owning the pass" isn't a purchase state here: both tracks are always
// unlocked, matching the spirit of "bought the game -> both sides".
// Premium carries the pet-focused rewards, per the explicit ask to keep
// pet stuff on one side only.
const BP_REWARDS_FREE = Array.from({ length: 40 }, (_, i) => {
  const n = i + 1;
  if (n % 20 === 0) return { type:'gem',    amount:20,          icon:'cur_gems', label:`${20} Gems` };
  if (n % 10 === 0) return { type:'shard',  amount:1,           icon:'rune',     label:`Shard` };
  if (n % 5  === 0) return { type:'slot',   amount:3,           icon:'cur_slot', label:`3 Coins` };
  if (n % 4  === 0) return { type:'gold',   amount:500+n*50,    icon:'cur_gold', label:`${500+n*50} Gold` };
  if (n % 3  === 0) return { type:'gem',    amount:5,           icon:'cur_gems', label:`5 Gems` };
  if (n % 2  === 0) return { type:'talent', amount:5,           icon:'cur_tp',   label:`5 TP` };
  return              { type:'gold',   amount:200+n*20,    icon:'cur_gold', label:`${200+n*20} Gold` };
});
const BP_REWARDS_PREMIUM = Array.from({ length: 40 }, (_, i) => {
  const n = i + 1;
  if (n % 20 === 0) return { type:'gem',     amount:50,           icon:'cur_gems', label:`${50} Gems` };
  if (n % 10 === 0) return { type:'petEgg',  amount:1,            icon:'ui_egg',   label:`Pet Egg` };
  if (n % 5  === 0) return { type:'petFood', amount:8,            icon:'petFood',  label:`8 Pet Food` };
  if (n % 4  === 0) return { type:'gold',    amount:900+n*70,     icon:'cur_gold', label:`${900+n*70} Gold` };
  if (n % 3  === 0) return { type:'gem',     amount:12,           icon:'cur_gems', label:`12 Gems` };
  if (n % 2  === 0) return { type:'talent',  amount:10,           icon:'cur_tp',   label:`10 TP` };
  return               { type:'petFood', amount:4,            icon:'petFood',  label:`4 Pet Food` };
});
function claimBpNode(save, setSave, track, i, reachedNode, e) {
  const claimedObj = normalizeBpClaimed(save);
  const claimed = claimedObj[track] || [];
  if (i > reachedNode || claimed.includes(i)) return;
  const rwd = (track === 'premium' ? BP_REWARDS_PREMIUM : BP_REWARDS_FREE)[i];
  const ns = {
    ...save,
    battlePass: { ...(save.battlePass || {}), claimed: { ...claimedObj, [track]: [...claimed, i] } },
  };
  applyReward(ns, bpRewardBag(rwd));
  if (rwd.type === 'petEgg') {
    ns.petEggs = [...(ns.petEggs || []), { rarity: 'common', type: 'aquaSprite' }];
  }
  setSave(ns);
  Audio.claimPing?.();
  fireClaimFx(e?.currentTarget, rwd, { flyTo: CAMP_CLAIM_FLY });
}

function claimAllBp(save, setSave, e) {
  const reached = battlePassReachedNode(save);
  const claimedObj = normalizeBpClaimed(save);
  const ns = { ...save, battlePass: { ...(save.battlePass || {}), claimed: { free: [...claimedObj.free], premium: [...claimedObj.premium] } } };
  const bag = {};
  let n = 0;
  for (const track of ['free', 'premium']) {
    const rewards = track === 'premium' ? BP_REWARDS_PREMIUM : BP_REWARDS_FREE;
    const claimed = ns.battlePass.claimed[track];
    for (let i = 0; i <= reached; i++) {
      if (claimed.includes(i)) continue;
      claimed.push(i);
      n++;
      const rwd = rewards[i];
      const piece = bpRewardBag(rwd);
      Object.entries(piece).forEach(([k, v]) => { bag[k] = (bag[k] || 0) + v; });
      if (rwd.type === 'petEgg') {
        ns.petEggs = [...(ns.petEggs || []), { rarity: 'common', type: 'aquaSprite' }];
      }
    }
  }
  if (!n) return;
  applyReward(ns, bag);
  setSave(ns);
  Audio.claimPing?.();
  fireClaimFx(e?.currentTarget, bag, { flyTo: CAMP_CLAIM_FLY });
}

export function BattlePassPanel({ save, setSave }) {
  const claimedObj = normalizeBpClaimed(save);
  const reachedNode = battlePassReachedNode(save);
  const unclaimed = battlePassUnclaimedCount(save);
  const rank = save.profile?.level || 1;

  const renderTrack = (track, rewards, claimedList, label, accentClass) => (
    <div className={`bp-track-wrap ${accentClass}`}>
      <div className="bp-track-label">{label}</div>
      <div className="bp-track" data-testid={`bp-track-${track}`}>
        {rewards.map((rwd, i) => {
          const reached   = i <= reachedNode;
          const isClaimed = claimedList.includes(i);
          const canClaim  = reached && !isClaimed;
          return (
            <div key={i} className={`bp-node ${reached?'reached':''} ${isClaimed?'claimed':''} ${canClaim?'claimable':''}`}
              onClick={(e) => canClaim && claimBpNode(save, setSave, track, i, reachedNode, e)} data-testid={`bp-node-${track}-${i}`}>
              <span className="bp-node-icon"><GameIcon name={rwd.icon} size={18} /></span>
              <span className="bp-node-label">{rwd.label}</span>
              {isClaimed && <span className="bp-check">CLAIMED</span>}
              {canClaim  && <span className="bp-glow-ring"/>}
              <span className="bp-node-num">{i+1}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="bp-panel" data-testid="bp-panel">
      <div className="bp-header">
        <span className="bp-title">SEASON PASS</span>
        <span className="bp-progress">Rank {rank} · node {reachedNode + 1}/{BP_NODE_COUNT}</span>
        {unclaimed > 0 && <span className="bp-unclaimed">{unclaimed} rewards ready</span>}
        {unclaimed > 0 && (
          <button className="patrol-btn claim" onClick={(e) => claimAllBp(save, setSave, e)} data-testid="bp-claim-all">CLAIM ALL</button>
        )}
      </div>
      <div className="bp-desc">Each account rank opens one node on both tracks. Leftover XP inside a rank does not count — the wrap is the clock.</div>
      {renderTrack('premium', BP_REWARDS_PREMIUM, claimedObj.premium || [], 'PREMIUM (pet rewards)', 'bp-premium')}
      {renderTrack('free', BP_REWARDS_FREE, claimedObj.free || [], 'FREE', 'bp-free')}
    </div>
  );
}

// ─── CODEX ────────────────────────────────────────────────
const ENEMY_LORE = {
  slime:'A gelatinous organism mutated by Lake contamination. Weak, but legion.',
  sludgeling:'Silt that learned to walk. First thing on the shore — three hits if all you have is the rib.',
  sludgeWarden:'The pond\'s own guardian, not the Eye. Eight hits of snapped station before it stays down.',
  briner:'Salt-crusted and fast. Two hits. Ripples after the first pond.',
  bogHusk:'A body the bog kept. Four hits. It does not hurry.',
  theDredger:'It dredges the shallows for anything that still moves. Twelve hits. The second pond\'s answer to a boss.',
  bat:'Voidbat — phase-shifted creature drawn to dimensional rifts. Fast and unpredictable.',
  brute:'Mutant humanoid reinforced by crystallized water. Slow but devastating.',
  ranger:'A corrupted soldier armed with liquid-nitrogen projectile launchers.',
  charger:'Void-energy overload turns this creature into a living battering ram.',
  ghoul:'Undead water-walker. Killed before — won\'t stay dead.',
  necron:'Necron: survived multiple extinction cycles. Treat with respect.',
  crystalite:'Crystal-armored insectoid. Energy attacks bounce off.',
  soulshard:'Fragmented consciousness crystallized into razor-sharp shards. Immune to knockback.',
  bonewalker:'Animated skeletal structure that regenerates faster than you can break it.',
  voidspawn:'Born from a void fracture. Rapid, vicious, no negotiation.',
  steelbrute:'Bio-mechanical juggernaut. Failed experiments on Brutes.',
  lightningbug:'Overcharged insectoid. When it dies, it discharges. Distance recommended.',
  techsoldier:'Military-grade automaton. Fires tracking rounds. Priority target.',
  nanoswarm:'Microscopic killer-bots organized into a lethal swarm.',
  bossOcular:'The Eye of Horus — an ancient surveillance construct awakened by the Lake.',
  bossNecromancer:'Nekromansen — a failed immortality experiment that feeds on death itself.',
  bossVoidTitan:'The Void Titan. Presence alone warps spacetime. The Lake is its egg.',
  bossAida:'A.I.D.A. — Adaptive Intelligence Defense Array. Created to protect. Has other plans.',
};

const ALL_ENEMY_IDS = Object.keys(ENEMIES).filter(id => !ENEMIES[id].boss);
const ALL_BOSS_IDS  = Object.keys(ENEMIES).filter(id =>  ENEMIES[id].boss);

export function CodexPanel({ save, setSave }) {
  const [tab, setTabC] = useState('enemies');
  const codex  = save.codex || {};
  const killed  = codex.enemies   || {};
  const weapons = codex.weapons   || {};
  const claimed = codex.claimedDiscoveries || [];
  const unclaimed = codexUnclaimedCount(save);
  const weaponIds = [...new Set([
    ...Object.keys(STARTER_WEAPONS || {}),
    ...Object.keys(weapons),
    ...Object.keys(WEAPONS),
  ])];

  const totalDiscovered = Object.keys(killed).length + Object.keys(weapons).length;

  // Proof-of-concept for the general "a claim should always be felt" rule
  // (Timmy: "vid claim alltid ska de visas å kännas å låta") — this used to
  // be a silent setSave with no sound, toast, or on-screen acknowledgement
  // at all. Scoped to Codex discovery claims first; the currency chip in
  // Camp's header already animates the number ticking up with a green
  // arrow (see `CountUp` in Camp.jsx) once `save.gems` changes, so this only
  // needs to add the sound/toast/local burst — the "flows into the current
  // total" half of the ask was already built, just never wired to a sound.
  const [burstId, setBurstId] = useState(null);
  const payDiscoveries = (ids) => {
    const ns = {
      ...save,
      codex: { ...save.codex, claimedDiscoveries: [...claimed, ...ids] },
    };
    applyReward(ns, { gems: ids.length });
    return ns;
  };
  const claimDiscovery = (id, e) => {
    if (claimed.includes(id)) return;
    setSave(payDiscoveries([id]));
    Audio.claimPing?.();
    fireClaimFx(e?.currentTarget, { gems: 1 }, { flyTo: CAMP_CLAIM_FLY });
    setBurstId(id);
    setTimeout(() => setBurstId(cur => (cur === id ? null : cur)), 750);
  };
  const claimAllCodex = (e) => {
    const ids = [...new Set([
      ...Object.keys(killed),
      ...Object.keys(weapons),
    ])].filter((id) => !claimed.includes(id));
    if (!ids.length) return;
    setSave(payDiscoveries(ids));
    Audio.claimPing?.();
    fireClaimFx(e?.currentTarget, { gems: ids.length }, { flyTo: CAMP_CLAIM_FLY });
  };

  const row = (id, { disc, name, lore, lockedLore, kills, killsTone, icon, boss }) => {
    const alrClaimed = claimed.includes(id);
    return (
      <div key={id} className={`codex-entry${boss ? ' boss' : ''} ${disc ? 'found' : 'undiscovered'}`} data-testid={`codex-${boss ? 'boss' : 'enemy'}-${id}`}>
        <div className="codex-entry-icon"><GameIcon name={icon} size={20} tone={disc ? undefined : '#3a2a5e'} /></div>
        <div className="codex-entry-info">
          <div className="codex-entry-name">{disc ? name : '???'}</div>
          <div className="codex-entry-lore">{disc ? lore : lockedLore}</div>
          {disc && <div className="codex-entry-kills" style={killsTone ? { color: killsTone } : undefined}>{kills}</div>}
        </div>
        {disc && !alrClaimed && (
          <button className={`codex-claim-btn${boss ? ' boss-claim' : ''}`} onClick={(e) => claimDiscovery(id, e)} data-testid={`codex-claim-${id}`}>
            <GameIcon name="cur_gems" size={12} /> +1
          </button>
        )}
        {disc && alrClaimed && <span className="codex-claimed-badge">CLAIMED</span>}
        {burstId === id && (
          <span className="codex-claim-burst"><GameIcon name="cur_gems" size={13} /> +1</span>
        )}
      </div>
    );
  };

  return (
    <div className="codex-panel" data-testid="codex-panel">
      <div className="codex-header">
        <span className="codex-title">CODEX</span>
        <span className="codex-count">{totalDiscovered} entries</span>
        {unclaimed > 0 && (
          <span className="codex-claim-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {unclaimed} unclaimed <GameIcon name="cur_gems" size={13} />
          </span>
        )}
        {unclaimed > 0 && (
          <button className="patrol-btn claim" onClick={claimAllCodex} data-testid="codex-claim-all">CLAIM ALL</button>
        )}
      </div>

      <div className="codex-tabs">
        {[
          ['enemies',  'ENEMIES'],
          ['bosses',   'BOSSES'],
          ['weapons',  'WEAPONS'],
        ].map(([k,l])=>(
          <button key={k} className={`codex-tab ${tab===k?'active':''}`} onClick={() => setTabC(k)}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'enemies' && (
        <div className="codex-grid">
          {ALL_ENEMY_IDS.map(id => {
            const e = ENEMIES[id];
            const disc = (killed[id]?.count || 0) > 0;
            return row(id, {
              disc, name: e.name, lore: ENEMY_LORE[id] || 'Unknown entity.',
              lockedLore: 'Kill this enemy to reveal.',
              kills: `Kills: ${killed[id]?.count || 0}×`,
              icon: 'ui_waterdrop',
            });
          })}
        </div>
      )}

      {tab === 'bosses' && (
        <div className="codex-grid">
          {ALL_BOSS_IDS.map(id => {
            const e = ENEMIES[id];
            const disc = (killed[id]?.count || 0) > 0;
            return row(id, {
              disc, name: e.name, lore: ENEMY_LORE[id] || 'A dangerous entity.',
              lockedLore: 'Defeat this boss to reveal lore.',
              kills: `Defeats: ${killed[id]?.count || 0}×`,
              killsTone: '#ff3146', icon: 'ui_void', boss: true,
            });
          })}
        </div>
      )}

      {tab === 'weapons' && (
        <div className="codex-grid">
          {weaponIds.map(wid => {
            const w = WEAPONS[wid] || STARTER_WEAPONS[wid] || { name: wid };
            const disc = (weapons[wid]?.count || 0) > 0;
            const alrClaimed = claimed.includes(wid);
            return (
              <div key={wid} className={`codex-entry ${disc ? 'found' : 'undiscovered'}`} data-testid={`codex-weapon-${wid}`}>
                <div className="codex-entry-icon">
                  <GameIcon name={w.icon || wid} size={20} tone={disc ? undefined : '#3a2a5e'} />
                </div>
                <div className="codex-entry-info">
                  <div className="codex-entry-name">{disc ? (w.name || wid) : '???'}</div>
                  <div className="codex-entry-lore">{disc ? (w.desc || 'A weapon of the Lake.') : 'Use this weapon in a run.'}</div>
                  {disc && <div className="codex-entry-kills" style={{ color: '#ffd166' }}>Runs: {weapons[wid]?.count || 0}×</div>}
                </div>
                {disc && !alrClaimed && (
                  <button className="codex-claim-btn" onClick={(e) => claimDiscovery(wid, e)} data-testid={`codex-claim-${wid}`}>
                    <GameIcon name="cur_gems" size={12} /> +1
                  </button>
                )}
                {disc && alrClaimed && <span className="codex-claimed-badge">CLAIMED</span>}
                {burstId === wid && (
                  <span className="codex-claim-burst"><GameIcon name="cur_gems" size={13} /> +1</span>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
